import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { ResearchTransactionFilterModelDto } from '@treasury/api/channel';
import { NavigationService } from '@treasury/core/navigation';
import { AccountsService } from '@treasury/domain/accounts';
import { TmContainer } from '@treasury/presentation';
import { DownloadPageType } from '@treasury/presentation/components/tm-download.types';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import { type AccountType } from '../data/account-research-transactions-filter-items';
import { ResearchedTransactionAccountVm } from '../data/researched-transaction-account-view-model';
import { ResearchedTransactionVm } from '../data/researched-transaction-view-model';

import '@treasury/presentation/components/tm-context-menu';
import '@treasury/presentation/components/tm-tabs';
import '../partials/account-research-transactions-accounts-table';
import '../partials/account-research-transactions-filter-sheet';
import '../partials/account-research-transactions-table';

interface RouteParams {
    accountType: string;
}

export const tagName = 'accounts-research-transactions-container';
@customElement(tagName)
export class AccountsResearchTransactionsContainer extends TmContainer {
    @InjectProperty()
    declare private readonly AccountsService: AccountsService;

    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @state()
    private transactions: ResearchedTransactionVm[] = [];

    @state()
    private accounts: ResearchedTransactionAccountVm[] = [];

    @state()
    private filterSheetOpen = false;

    @state()
    private initialAccountType?: AccountType;

    @state()
    private filters?: ResearchTransactionFilterModelDto & { transactionDateType: string };

    @state()
    private tabs = [
        { label: 'Accounts', id: 'accounts' },
        { label: 'Transactions', id: 'transactions' },
    ];

    @state()
    private selectedTab = 'accounts';

    private get subtitle() {
        const from = new Date(`${this.filters?.transactionDateFrom}T00:00:00`);
        const to = new Date(`${this.filters?.transactionDateTo}T00:00:00`);
        if (this.filters) {
            switch (this.filters.transactionDateType) {
                case 'Today':
                    return 'Today';
                case 'Week to Date':
                    return 'Week to Date';
                case 'Month to Date':
                    return 'Month to Date';
                case 'Year to Date':
                    return 'Year to Date';
                case 'Specific Date':
                    return `${format(from, 'MM/dd/yyyy')}`;
                default:
                    return `${format(from, 'MM/dd/yyyy')} - ${format(to, 'MM/dd/yyyy')}`;
            }
        } else {
            return undefined;
        }
    }

    public async connectedCallback() {
        super.connectedCallback();
        if (this.deviceService.isDesktop) {
            this.tabs = [
                { label: 'Transactions', id: 'transactions' },
                { label: 'Accounts', id: 'accounts' },
            ];
            this.selectedTab = 'transactions';
        }
        this.configureHeader();
        const { params } = await this.navService.getRouteData<RouteParams>();
        if (params.accountType) {
            //remove the potential plural 's' from the account type
            this.initialAccountType = params.accountType.slice(0, -1) as AccountType;
        }
    }

    protected render() {
        return html`<tm-section
                class=${classMap({
                    'mx-2 my-14 rounded-lg py-2 bg-primary h-[50%] overflow-hidden': this.isDesktop,
                })}
            >
                ${this.renderTabs()}${this.renderTable()}
            </tm-section>
            <account-research-transactions-filter-sheet
                .open=${this.filterSheetOpen}
                .initialAccountType=${this.initialAccountType}
                @applyFilters=${(e: CustomEvent) => {
                    this.filters = e.detail;
                    this.configureHeader();
                    this.loadTransactions();
                }}
                @close=${() => {
                    this.filterSheetOpen = false;
                }}
            ></account-research-transactions-filter-sheet>`;
    }

    protected renderTable() {
        switch (this.selectedTab) {
            case 'accounts':
                return html`<account-research-transactions-accounts-table
                    .accounts=${this.accounts}
                    .transactionDateRange=${this.subtitle}
                    .loading=${this.loading}
                    @filter-transactions=${() => {
                        this.filterSheetOpen = true;
                    }}
                ></account-research-transactions-accounts-table>`;
            case 'transactions':
                return html`<account-research-transactions-table
                    .isDesktop=${this.deviceService.isDesktop}
                    .transactions=${this.transactions}
                    .loading=${this.loading}
                    @filter-transactions=${() => {
                        this.filterSheetOpen = true;
                    }}
                ></account-research-transactions-table>`;
            default:
                return nothing;
        }
    }

    protected renderTabs() {
        return html`<div class="bg-primary">
            <tm-tabs
                .tabs=${this.tabs}
                .activeTab=${this.selectedTab}
                @switchTab=${(e: CustomEvent) => {
                    this.selectedTab = e.detail.activeTab;
                }}
            ></tm-tabs>
        </div>`;
    }

    private configureHeader() {
        this.headerService.configure({
            title: 'Research Transactions',
            subtitle: this.subtitle,
            backAction: () => this.navService.navigate(`/accounts`),
        });
    }

    private async loadTransactions() {
        if (!this.filters) {
            throw new Error('Could not load transactions. No filters were present.');
        }

        this.loading = true;
        try {
            this.transactions = (await this.AccountsService.researchTransactions(this.filters)).map(
                t => new ResearchedTransactionVm(t)
            );
            const accounts = [] as Array<ResearchedTransactionVm[]>;
            this.transactions.forEach(t => {
                const existingAccount = accounts.find(
                    (a: ResearchedTransactionVm[]) => a[0].accountNumber === t.accountNumber
                );
                if (existingAccount) {
                    existingAccount.push(t);
                } else {
                    accounts.push([t]);
                }
            });
            this.accounts = accounts.map(a => new ResearchedTransactionAccountVm(a));
            this.addActionsToHeader();
            this.setTabs();
        } catch {
            this.notificationService.renderError('Failed to load transactions');
        } finally {
            this.loading = false;
        }
    }

    private setTabs() {
        if (this.deviceService.isDesktop) {
            this.tabs = [
                { label: `Transactions (${this.transactions.length})`, id: 'transactions' },
                { label: `Accounts (${this.accounts.length})`, id: 'accounts' },
            ];
        } else {
            this.tabs = [
                { label: `Accounts (${this.accounts.length})`, id: 'accounts' },
                { label: `Transactions (${this.transactions.length})`, id: 'transactions' },
            ];
        }
    }

    private addActionsToHeader() {
        this.headerService.configure({
            title: 'Research Transactions',
            subtitle: this.subtitle,
            backAction: () => this.navService.navigate(`/accounts`),
            menuItems: [
                {
                    title: 'Download',
                    icon: html`<tm-download
                        .page=${DownloadPageType.ResearchTransactions}
                        .filterModel=${this.filters}
                        .disabled=${!this.transactions.length}
                    ></tm-download>`,
                    action: () => undefined,
                },
            ],
        });
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AccountsResearchTransactionsContainer;
    }
}
